import { Button } from '@synoptic/ui-kit/button/button.tsx';
import { Input } from '@synoptic/ui-kit/input/input.tsx';
import { useToast } from '@synoptic/ui-kit/toast/toast-provider.tsx';
import { resetPassword } from 'aws-amplify/auth';
import { FC, useRef } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router';
import { Link } from '@/routes-utils/navigation.tsx';
import { paths } from '@/routes-utils/paths';
import { sanitizeRedirectParam } from '@/routes-utils/sanitize-redirect-param';
import {
  actionLinkButton,
  cardBody,
  formFieldsContainer,
  formHeading,
  submitAuthFormButton,
} from '../_auth/page.css';

type FormInputs = {
  username: string;
};

export const ResetPasswordForm: FC<{
  onResetPassword: (username: string) => void;
}> = ({ onResetPassword }) => {
  const [searchParams] = useSearchParams();
  const fromParam = sanitizeRedirectParam(searchParams.get('from'));
  const oauthParam = searchParams.get('oauth') ?? undefined;
  const usernameParam = searchParams.get('username') ?? '';

  const { handleSubmit, formState, reset, register } = useForm<FormInputs>({
    defaultValues: { username: usernameParam },
  });
  const formRef = useRef<HTMLFormElement>(null);
  const toast = useToast();

  const submit: SubmitHandler<FormInputs> = async (data) => {
    const { username } = data;
    try {
      await resetPassword({
        username,
      });

      onResetPassword(username);
      reset();
    } catch (e) {
      toast.error({ title: String(e) });
    }
  };

  return (
    <form ref={formRef} onSubmit={handleSubmit(submit)}>
      <h1 className={formHeading}>Reset your password</h1>
      <p className={cardBody}>
        Enter your username and we will send you instructions to reset your
        password.
      </p>
      <div className={formFieldsContainer}>
        <Input
          {...register('username', {
            required: true,
          })}
          placeholder={'Enter your email or username'}
          label={'Email or username'}
          autoFocus
        />
        <Button
          className={submitAuthFormButton}
          size={'large'}
          type="submit"
          loading={formState.isSubmitting}
        >
          Continue
        </Button>
        <Button
          disabled={formState.isSubmitting}
          size={'large'}
          variant="ghost"
          asChild
        >
          <Link
            className={actionLinkButton}
            to={paths.makeLogin(fromParam, oauthParam)}
          >
            Back
          </Link>
        </Button>
      </div>
    </form>
  );
};
